<template>
   <b-modal
    ref="modal"
    :title="modalTitle"
    size="lg"
    no-close-on-backdrop
    :hide-header-close="phase === 'processing' || phase === 'submitting'"
    :no-close-on-esc="phase === 'processing' || phase === 'submitting'"
    hide-footer
    @hidden="onHidden"
    >
    <div class="bulk-issue-shell">
       <BulkIssueOverview
        :total-amount-text="totalAmountText"
        :stats="overviewStats"
      /> <BulkIssueStateBanner
        :phase="phase"
        :error-message="bulkIssueError"
        :blocked-count="blockedCount"
      /> <BulkIssueValidationControl
        v-if="phase === 'review'"
        :active-view="activeValidationView"
        :eligible-count="eligibleCount"
        :blocked-count="blockedCount"
        @change="setValidationView"
      />
      <div class="bulk-issue-shell__surface">
         <BulkIssueEligibleTable
          v-if="phase === 'review' && activeValidationView === 'eligible'"
          :items="eligibleList"
          :selected-ids="selectedEligibleIds"
          :format-currency="formatCurrency"
          @toggle="toggleEligible"
          @select-all="selectAllEligible"
          @deselect-all="deselectAllEligible"
        /> <BulkIssueBlockedTable
          v-else-if="phase === 'review' && activeValidationView === 'blocked'"
          :items="filteredBlockedList"
          :filter="blockedFilter"
          :filter-options="blockedFilterOptions"
          @filter-change="setBlockedFilter"
        /> <BulkIssueBlockedTable
          v-else-if="phase === 'zeroEligible'"
          :items="filteredBlockedList"
          :filter="blockedFilter"
          :filter-options="blockedFilterOptions"
          @filter-change="setBlockedFilter"
        /> <BulkIssueProcessingState
          v-else-if="phase === 'submitting' || phase === 'processing'"
          :progress-percent="progressPercent"
          :processed="progress.processed"
          :total="progress.total"
          :current-step-label="currentStepLabel"
        />
        <div
          v-else-if="phase === 'result'"
          class="bulk-issue-shell__result"
        >
           <BulkIssueResultState
            :result="result"
            :is-unknown-result="isUnknownResult"
          />
          <div
            v-if="result.failed.length > 0"
            class="bulk-issue-shell__result-actions"
          >
             <b-button
              size="sm"
              variant="outline-secondary"
              class="mr-2"
              @click="downloadErrors"
              > <i class="fas fa-download mr-1"></i> Tải danh sách lỗi </b-button
            > <b-button
              size="sm"
              variant="outline-warning"
              @click="retryFailed"
              > <i class="fas fa-redo mr-1"></i> Thử lại {{ result.failed.length }} HĐ thất bại
              </b-button
            >
          </div>

        </div>

      </div>
       <BulkIssueActionFooter
        v-if="showFooter"
        :summary="footerSummary"
        :cancel-label="footerCancelLabel"
        :primary-label="footerPrimaryLabel"
        :primary-disabled="footerPrimaryDisabled"
        :show-primary="showFooterPrimary"
        @cancel="handleFooterCancel"
        @confirm="handleFooterConfirm"
      />
    </div>
     </b-modal
  >
</template>

<script>
  import { mapGetters } from 'vuex'
  import ApiService from '@/core/services/api.service'
  import {
    EINVOICE_BULK_ISSUE_PRECHECK,
    EINVOICE_BULK_ISSUE_PROGRESS,
    EINVOICE_BULK_ISSUE_SUBMIT,
  } from '@/core/services/store/modules/einvoice/einvoice.module'
  import einvoiceMixin from '../mixins/einvoice.mixin'
  import { getStepLabel } from '../utils/bulkIssue.utils'
  import BulkIssueActionFooter from './BulkIssueActionFooter.vue'
  import BulkIssueBlockedTable from './BulkIssueBlockedTable.vue'
  import BulkIssueEligibleTable from './BulkIssueEligibleTable.vue'
  import BulkIssueOverview from './BulkIssueOverview.vue'
  import BulkIssueProcessingState from './BulkIssueProcessingState.vue'
  import BulkIssueResultState from './BulkIssueResultState.vue'
  import BulkIssueStateBanner from './BulkIssueStateBanner.vue'
  import BulkIssueValidationControl from './BulkIssueValidationControl.vue'
  import {
    buildBulkIssueDecisionSummary,
    buildBulkIssueFilterOptions,
    buildBulkIssueOverviewStats,
    collectBulkIssueSalesInvoiceIds,
    createBulkIssueRetryPayload,
    filterBlockedBulkIssueItems,
    getBulkIssuePrecheckSummary,
    normalizeBulkIssueInvoiceType,
    sumSelectedEligibleAmount,
  } from './bulkIssueModal.logic'

  const POLL_INTERVAL_MS = 2000
  const POLL_TIMEOUT_MS = 120000

  export default {
    name: 'BulkIssueModal',
    components: {
      BulkIssueActionFooter,
      BulkIssueBlockedTable,
      BulkIssueEligibleTable,
      BulkIssueOverview,
      BulkIssueProcessingState,
      BulkIssueResultState,
      BulkIssueStateBanner,
      BulkIssueValidationControl,
    },
    mixins: [einvoiceMixin],
    data() {
      return {
        activeValidationView: 'eligible',
        selectedEligibleIds: [],
        blockedFilter: null,
        pollTimer: null,
        pollStartedAt: 0,
        pollSessionId: null,
      }
    },
    computed: {
      ...mapGetters(['einvoiceBulkIssue']),
      phase() {
        return this.einvoiceBulkIssue.phase || 'idle'
      },
      preCheckResult() {
        return (
          this.einvoiceBulkIssue.preCheckResult || {
            action: null,
            invoiceType: null,
            total: 0,
            eligible: [],
            blocked: [],
            summary: {
              eligible: 0,
              blocked: 0,
              eligibleTotalAmount: 0,
            },
          }
        )
      },
      bulkIssueError() {
        return this.einvoiceBulkIssue.error || ''
      },
      requestPayload() {
        return this.einvoiceBulkIssue.requestPayload || null
      },
      eligibleList() {
        return this.preCheckResult.eligible
      },
      blockedList() {
        return this.preCheckResult.blocked
      },
      preCheckSummary() {
        return getBulkIssuePrecheckSummary(this.preCheckResult)
      },
      totalSelected() {
        return this.preCheckSummary.total
      },
      eligibleCount() {
        return this.preCheckSummary.eligible
      },
      blockedCount() {
        return this.preCheckSummary.blocked
      },
      eligibleTotalAmount() {
        return this.preCheckSummary.eligibleTotalAmount
      },
      totalAmountText() {
        return `${this.formatCurrency(this.eligibleTotalAmount)} ₫`
      },
      overviewStats() {
        return buildBulkIssueOverviewStats({
          totalSelected: this.totalSelected,
          eligibleCount: this.eligibleCount,
          blockedCount: this.blockedCount,
          eligibleTotalAmount: this.eligibleTotalAmount,
          formatCurrency: this.formatCurrency,
        })
      },
      blockedFilterOptions() {
        return buildBulkIssueFilterOptions(this.blockedList)
      },
      filteredBlockedList() {
        return filterBlockedBulkIssueItems(this.blockedList, this.blockedFilter)
      },
      selectedEligibleCount() {
        return this.selectedEligibleIds.length
      },
      selectedEligibleAmount() {
        return sumSelectedEligibleAmount(this.eligibleList, this.selectedEligibleIds)
      },
      progress() {
        return (
          this.einvoiceBulkIssue.progress || {
            processed: 0,
            total: 0,
            currentStep: null,
            status: null,
          }
        )
      },
      progressPercent() {
        if (!this.progress.total) return 0
        return Math.round((this.progress.processed / this.progress.total) * 100)
      },
      currentStepLabel() {
        return getStepLabel(this.progress.currentStep)
      },
      result() {
        return this.einvoiceBulkIssue.result || { succeeded: [], failed: [], skipped: 0 }
      },
      isUnknownResult() {
        return (
          this.progress.status === 'UNKNOWN' &&
          !this.result.succeeded.length &&
          !this.result.failed.length
        )
      },
      jobId() {
        return this.einvoiceBulkIssue.jobId
      },
      modalTitle() {
        return this.phase === 'result' ? 'Kết quả phát hành' : 'Phát hành hàng loạt'
      },
      showFooter() {
        return ['review', 'zeroEligible', 'error', 'result'].includes(this.phase)
      },
      footerSummary() {
        if (this.phase === 'zeroEligible') {
          return {
            title: 'Chưa có hóa đơn hợp lệ để phát hành',
            amount: `Tổng giá trị ${this.formatCurrency(this.eligibleTotalAmount)} ₫`,
            blockedHint: `${this.blockedCount} hóa đơn bị chặn, không nằm trong lượt này`,
          }
        }

        if (this.phase === 'error') {
          return {
            title: 'Kiểm tra trước phát hành chưa hoàn tất',
            amount: 'Rà soát lỗi rồi thử lại sau',
            blockedHint: '',
          }
        }

        if (this.phase === 'result') {
          return {
            title: 'Đã xử lý xong lượt phát hành',
            amount: `Thành công ${this.result.succeeded.length}, thất bại ${this.result.failed.length}`,
            blockedHint: '',
          }
        }

        return buildBulkIssueDecisionSummary({
          selectedEligibleCount: this.selectedEligibleCount,
          blockedCount: this.blockedCount,
          selectedEligibleAmount: this.selectedEligibleAmount,
          formatCurrency: this.formatCurrency,
        })
      },
      footerCancelLabel() {
        return this.phase === 'result' ? 'Đóng và làm mới danh sách' : 'Hủy'
      },
      footerPrimaryLabel() {
        return `Phát hành ${this.selectedEligibleCount} hóa đơn hợp lệ`
      },
      footerPrimaryDisabled() {
        return this.phase !== 'review' || this.selectedEligibleCount === 0
      },
      showFooterPrimary() {
        return this.phase === 'review'
      },
    },
    methods: {
      resetLocalState() {
        this.activeValidationView = 'eligible'
        this.selectedEligibleIds = []
        this.blockedFilter = null
      },
      async open(payload) {
        this.stopPolling()
        this.resetLocalState()
        try {
          normalizeBulkIssueInvoiceType(payload?.invoiceType)
        } catch (error) {
          this.$store.commit('SET_BULK_ISSUE_ERROR', error?.message || 'invoiceType không hợp lệ')
          this.activeValidationView = 'blocked'
          return
        }

        try {
          this.$refs.modal.show()
          const result = await this.$store.dispatch(EINVOICE_BULK_ISSUE_PRECHECK, payload)
          this.selectedEligibleIds = collectBulkIssueSalesInvoiceIds(result.eligible)
          this.activeValidationView = result.summary?.eligible > 0 ? 'eligible' : 'blocked'
        } catch {
          this.activeValidationView = 'blocked'
        }
      },
      hide() {
        this.$refs.modal.hide()
      },
      onHidden() {
        this.stopPolling()
        this.resetLocalState()
        this.$store.commit('RESET_BULK_ISSUE')
      },
      setValidationView(view) {
        this.activeValidationView = view
      },
      setBlockedFilter(filter) {
        this.blockedFilter = filter
      },
      toggleEligible(item) {
        const salesInvoiceId = String(item.salesInvoiceId)
        const idx = this.selectedEligibleIds.findIndex((value) => value === salesInvoiceId)
        if (idx >= 0) {
          this.selectedEligibleIds.splice(idx, 1)
        } else {
          this.selectedEligibleIds.push(salesInvoiceId)
        }
      },
      selectAllEligible() {
        this.selectedEligibleIds = collectBulkIssueSalesInvoiceIds(this.eligibleList)
      },
      deselectAllEligible() {
        this.selectedEligibleIds = []
      },
      async confirmIssue() {
        if (this.selectedEligibleCount === 0) return

        const invoiceType =
          this.requestPayload?.invoiceType || this.preCheckResult.invoiceType || null
        if (!invoiceType) {
          return
        }

        try {
          const { jobId } = await this.$store.dispatch(EINVOICE_BULK_ISSUE_SUBMIT, {
            invoiceType,
            salesInvoiceIds: this.selectedEligibleIds,
          })
          this.startPolling(jobId)
        } catch {
          // store phase/error already owns the terminal error state
        }
      },
      startPolling(jobId) {
        const normalizedJobId = String(jobId || '').trim()
        if (!normalizedJobId) {
          return
        }

        this.stopPolling()
        this.pollStartedAt = Date.now()
        this.pollSessionId = `${this.pollStartedAt}-${Math.random().toString(36).slice(2, 10)}`
        this.pollOnce(this.pollSessionId, normalizedJobId)
      },
      isPollSessionActive(sessionId) {
        return Boolean(sessionId) && this.pollSessionId === sessionId
      },
      scheduleNextPoll(sessionId, jobId, delayMs = POLL_INTERVAL_MS) {
        if (!this.isPollSessionActive(sessionId)) {
          return
        }

        this.pollTimer = setTimeout(() => {
          this.pollTimer = null
          this.pollOnce(sessionId, jobId)
        }, delayMs)
      },
      async pollOnce(sessionId, jobId) {
        if (!this.isPollSessionActive(sessionId) || !jobId) {
          return
        }

        if (Date.now() - this.pollStartedAt > POLL_TIMEOUT_MS) {
          this.stopPolling(sessionId)
          return
        }

        try {
          const progress = await this.$store.dispatch(EINVOICE_BULK_ISSUE_PROGRESS, jobId)
          if (!this.isPollSessionActive(sessionId)) {
            return
          }

          if (progress?.isTerminal) {
            this.stopPolling(sessionId)
            return
          }

          this.scheduleNextPoll(sessionId, jobId)
        } catch (error) {
          if (!this.isPollSessionActive(sessionId)) {
            return
          }

          if (error?.isRetryable) {
            this.scheduleNextPoll(sessionId, jobId)
            return
          }

          this.stopPolling(sessionId)
        }
      },
      stopPolling(sessionId = null) {
        if (sessionId && sessionId !== this.pollSessionId) {
          return
        }

        if (this.pollTimer) {
          clearTimeout(this.pollTimer)
          this.pollTimer = null
        }

        this.pollStartedAt = 0
        this.pollSessionId = null
      },
      retryFailed() {
        try {
          const invoiceType = this.requestPayload?.invoiceType || this.preCheckResult.invoiceType
          const retryPayload = createBulkIssueRetryPayload(this.result.failed, invoiceType)
          if (!retryPayload.salesInvoiceIds.length) {
            return
          }

          this.open(retryPayload)
        } catch (error) {
          this.$store.commit(
            'SET_BULK_ISSUE_ERROR',
            error?.message || 'Không thể phát hành lại batch lỗi.',
          )
          return
        }
      },
      handleFooterCancel() {
        if (this.phase === 'result') {
          this.closeAndRefresh()
          return
        }

        this.hide()
      },
      handleFooterConfirm() {
        this.confirmIssue()
      },
      closeAndRefresh() {
        this.hide()
        this.$emit('closed')
      },
      async downloadErrors() {
        const jobId = this.jobId
        if (!jobId) return
        try {
          const response = await ApiService.get(`v1/einvoice/bulk-issue/export-errors/${jobId}`, {
            responseType: 'blob',
          })
          const url = URL.createObjectURL(new Blob([response.data]))
          const a = document.createElement('a')
          a.href = url
          a.download = `bulk-issue-errors-${jobId}.xlsx`
          a.click()
          URL.revokeObjectURL(url)
        } catch {
          // non-critical
        }
      },
    },
    beforeDestroy() {
      this.stopPolling()
    },
  }
</script>

<style scoped>
  .bulk-issue-shell {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    min-height: 420px;
  }

  .bulk-issue-shell__surface {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    min-height: 0;
  }

  .bulk-issue-shell__result {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  .bulk-issue-shell__result-actions {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    flex-wrap: wrap;
  }

  @media (max-width: 767.98px) {
    .bulk-issue-shell__result-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .bulk-issue-shell__result-actions > * {
      margin-right: 0 !important;
      width: 100%;
    }
  }
</style>

